import { z } from 'zod';

export const MAX_CLIENT_SIDE_FORM_FIELDS = 10;

export const formFieldTypeSchema = z.enum([
  'text',
  'email',
  'phone',
  'number',
  'date',
  'select',
  'textarea',
]);

export type FormFieldType = z.infer<typeof formFieldTypeSchema>;

export const formFieldSchema = z
  .object({
    name: z
      .string()
      .min(1)
      .max(60)
      .regex(
        /^[a-z][\d_a-z]*$/,
        'Lowercase letters, digits and underscores; must start with a letter',
      ),
    label: z.string().min(1).max(120),
    type: formFieldTypeSchema,
    required: z.boolean().default(false),
    options: z.array(z.string().min(1)).optional(),
    placeholder: z.string().max(200).optional(),
    validation: z.string().optional(),
  })
  .superRefine((field, ctx) => {
    if (
      field.type === 'select' &&
      (!field.options || field.options.length < 2)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['options'],
        message: 'Select fields require at least two options',
      });
    }
  });

export type FormField = z.infer<typeof formFieldSchema>;

export const formPresentationSchema = z.enum(['inline', 'overlay']);

export type FormPresentation = z.infer<typeof formPresentationSchema>;

export const showFormArtifactSchema = z.object({
  type: z.literal('form'),
  title: z.string(),
  description: z.string().optional(),
  fields: z.array(formFieldSchema).min(1).max(MAX_CLIENT_SIDE_FORM_FIELDS),
  submitLabel: z.string().default('Submit'),
  presentation: formPresentationSchema.default('inline'),
  disableDismiss: z.boolean().default(false),
});

/** Sent as toolResponse.data when the user closes a dismissable form. */
export const clientSideFormCanceledData = { canceled: true } as const;

export function isClientSideFormCanceled(
  data: Record<string, unknown>,
): boolean {
  return data.canceled === true;
}

export type ShowFormArtifact = z.infer<typeof showFormArtifactSchema>;

export const clientSideArtifactSchema = showFormArtifactSchema;

export type ClientSideArtifact = z.infer<typeof clientSideArtifactSchema>;

export function isShowFormArtifact(value: unknown): value is ShowFormArtifact {
  return showFormArtifactSchema.safeParse(value).success;
}

/** Widget → ai-core payload when the user submits a client-side form. */
export const clientSideToolResponseSchema = z.object({
  toolName: z.string().min(1),
  toolCallId: z.string().min(1),
  data: z.record(z.unknown()),
});

export type ClientSideToolResponse = z.infer<
  typeof clientSideToolResponseSchema
>;

export const CLIENT_SIDE_FORM_INTERRUPT_TYPE = 'client_side_form' as const;

/**
 * Payload passed to LangGraph's `interrupt()` by client-side form tools.
 * The graph pauses inside the tool node until the widget resumes it with
 * the submitted form data.
 *
 * `type: 'tool'` is LangChain's canonical tool-interrupt discriminator —
 * its stream transformer recognizes the envelope and skips the bogus
 * per-call error rejection a foreign shape triggers. Our own
 * discriminator lives in `kind`.
 */
export const clientSideFormInterruptSchema = z.object({
  type: z.literal('tool'),
  kind: z.literal(CLIENT_SIDE_FORM_INTERRUPT_TYPE),
  toolName: z.string().min(1),
  artifact: showFormArtifactSchema,
});

export type ClientSideFormInterrupt = z.infer<
  typeof clientSideFormInterruptSchema
>;

/** Pre-canonical shape — only in checkpoints saved before the cutover. */
const legacyClientSideFormInterruptSchema = z.object({
  type: z.literal(CLIENT_SIDE_FORM_INTERRUPT_TYPE),
  toolName: z.string().min(1),
  artifact: showFormArtifactSchema,
});

export function parseClientSideFormInterrupt(
  value: unknown,
): ClientSideFormInterrupt | null {
  const parsed = clientSideFormInterruptSchema.safeParse(value);

  if (parsed.success) {
    return parsed.data;
  }

  const legacy = legacyClientSideFormInterruptSchema.safeParse(value);

  return legacy.success
    ? { ...legacy.data, type: 'tool', kind: CLIENT_SIDE_FORM_INTERRUPT_TYPE }
    : null;
}

const FORM_CANCELED_CONTENT = 'User canceled the form without submitting.';

/**
 * Field names are constrained to /^[a-z][\d_a-z]*$/, so a key with leading
 * underscores can never collide with submitted form data.
 */
const FORM_USER_MESSAGE_KEY = '__userMessage';

/**
 * Resume payload used when the user sends a chat message instead of
 * interacting with the pending form. The form tool resolves as dismissed
 * and the agent answers the message in the same run.
 */
export function buildClientSideFormUserMessageData(
  message: string,
): Record<string, unknown> {
  return { [FORM_USER_MESSAGE_KEY]: message };
}

function getClientSideFormUserMessage(
  data: Record<string, unknown>,
): string | null {
  const value = data[FORM_USER_MESSAGE_KEY];

  return typeof value === 'string' ? value : null;
}

/**
 * Model-facing and persisted content of a resolved form tool call.
 * Round-trips through `parseClientSideFormToolResultContent`.
 */
export function formatClientSideFormToolResultContent(
  data: Record<string, unknown>,
): string {
  if (isClientSideFormCanceled(data)) {
    return FORM_CANCELED_CONTENT;
  }

  const userMessage = getClientSideFormUserMessage(data);

  if (userMessage !== null) {
    return `User did not fill in the form and sent a message instead: ${JSON.stringify(userMessage)}`;
  }

  return JSON.stringify(data);
}

/**
 * Frames a form response as a user message. Only used as fallback when the
 * agent's checkpoint expired and the response can't resume the paused graph.
 */
export function formatClientSideFormResponseAsUserMessage(
  toolName: string,
  data: Record<string, unknown>,
): string {
  if (isClientSideFormCanceled(data)) {
    return `I dismissed the "${toolName}" form without submitting it.`;
  }

  return `I submitted the "${toolName}" form with these values: ${JSON.stringify(data)}`;
}

export function parseClientSideFormToolResultContent(
  content: unknown,
): Record<string, unknown> | 'canceled' | null {
  if (content === null || content === undefined || content === '') {
    return null;
  }

  if (content === FORM_CANCELED_CONTENT) {
    return 'canceled';
  }

  /**
   * The stream pipeline parses JSON tool contents before persisting, so
   * the submission can arrive either as a JSON string (written at
   * submission time) or as the already-parsed object (tool result event).
   */
  if (typeof content === 'object' && !Array.isArray(content)) {
    return content as Record<string, unknown>;
  }

  if (typeof content !== 'string') {
    return null;
  }

  try {
    const parsed = JSON.parse(content) as unknown;

    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      !Array.isArray(parsed)
    ) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return null;
  }

  return null;
}
